import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// 1. Import City and Country data from the library
import { City, Country } from 'country-state-city';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const BUSINESS_TYPES = [
  { type: "Food & Beverage", subtypes: ["Restaurant","Café","Bakery","Ice Cream Parlour","Fast Food","Food Truck","Juice Bar","Catering Service","Tea House","Cloud Kitchen","Bar & Lounge"] },
  { type: "Retail", subtypes: ["Clothing Store","Grocery Store","Electronics Store","Furniture Store","Jewelry Store","Bookstore","Toy Store","Pet Store","Pharmacy","Convenience Store"] },
  { type: "Beauty & Wellness", subtypes: ["Hair Salon","Barber Shop","Spa","Nail Salon","Aesthetic Clinic","Skincare Clinic","Massage Center","Makeup Studio","Tattoo Studio","Cosmetic Clinic"] },
  { type: "Healthcare", subtypes: ["Hospital","Dental Clinic","Medical Clinic","Physiotherapy Center","Diagnostic Lab","Eye Clinic","Mental Health Center","Veterinary Clinic","Pharmacy"] },
  { type: "Fitness", subtypes: ["Gym","Yoga Studio","Personal Training","Martial Arts Academy","Sports Club","Swimming School","Dance Studio","Pilates Studio","CrossFit Center"] },
  { type: "Education", subtypes: ["School","College","Coaching Center","Tuition Center","Online Course Platform","Language Institute","Music Academy","Training Institute","Driving School"] },
  { type: "Professional Services", subtypes: ["Law Firm","Accounting Firm","Marketing Agency","IT Consultancy","Recruitment Agency","Architecture Firm","Business Consultancy","Tax Services","Translation Service"] },
  { type: "Hospitality", subtypes: ["Hotel","Resort","Hostel","Guest House","Travel Agency","Tour Operator","Vacation Rental","Event Venue","Conference Center"] },
  { type: "Home Services", subtypes: ["Cleaning Service","Plumbing Service","Electrical Service","Pest Control","Appliance Repair","HVAC Service","Landscaping","Interior Design","Moving Service"] },
  { type: "E-commerce", subtypes: ["Online Store","Marketplace Seller","Dropshipping Business","Subscription Box","Digital Products Store","Print-on-Demand Store","Online Reseller"] },
  { type: "Entertainment", subtypes: ["Event Management","Photography Studio","Gaming Arcade","Cinema","Music Studio","Podcast Studio","Media Company","Wedding Services","Production House"] },
  { type: "Technology", subtypes: ["Software Company","Mobile App Development","Web Design Agency","SaaS Platform","AI Startup","Cybersecurity Firm","IT Support","Hosting Provider","Data Analytics"] },
  { type: "Other", subtypes: ["Nonprofit Organization","Manufacturing","Agriculture","Logistics","Automotive","Wholesale Business","Religious Organization","Government Service","Miscellaneous"] },
];

// Your custom search dropdown component (Unchanged UI, wrapper optimized for external text control)
function SearchDropdown({ label, placeholder, options, value, onChange, disabled }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  const filtered = options.filter(o => o.toLowerCase().includes(query.toLowerCase())).slice(0, 50); // Slice to 50 for rendering performance

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const handleSelect = (opt) => {
    onChange(opt);
    setQuery(opt);
    setOpen(false);
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setOpen(true);
  };

  const handleClear = () => {
    setQuery('');
    onChange('');
    setOpen(false);
  };

  return (
      <div className="form-group" ref={ref} style={{ position: 'relative' }}>
        <label style={{ color: '#fff' }}>{label}</label>
        <div style={{ position: 'relative' }}>
          <input
              className="form-control"
              placeholder={placeholder}
              value={query}
              onChange={handleInputChange}
              onFocus={() => !disabled && setOpen(true)}
              disabled={disabled}
              autoComplete="off"
              style={{ paddingRight: 36, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'text' }}
          />
          <span
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: query ? 'pointer' : 'default', fontSize: 14, color: 'rgba(255,255,255,0.6)', userSelect: 'none', pointerEvents: query ? 'auto' : 'none' }}
              onClick={query ? handleClear : undefined}
          >
          {query ? '✕' : '⌕'}
        </span>
        </div>

        {open && !disabled && filtered.length > 0 && (
            <ul style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 999, margin: 0, padding: '4px 0', listStyle: 'none', background: 'rgba(30, 10, 50, 0.97)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 8, backdropFilter: 'blur(12px)', maxHeight: 200, overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
              {filtered.map((opt) => (
                  <li key={opt} onMouseDown={() => handleSelect(opt)} style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 14, color: value === opt ? '#ff2e58' : 'rgba(255,255,255,0.88)', fontWeight: value === opt ? 600 : 400, background: value === opt ? 'rgba(255,46,88,0.12)' : 'transparent', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,46,88,0.18)'} onMouseLeave={e => e.currentTarget.style.background = value === opt ? 'rgba(255,46,88,0.12)' : 'transparent'}>
                    {opt}
                  </li>
              ))}
            </ul>
        )}
      </div>
  );
}

export default function TenantRegister() {
  const { login } = useAuth();
  const navigate = useNavigate();

  // Added city, country, and a combined display field to state
  const [form, setForm] = useState({
    name: '', email: '', password: '', businessName: '',
    businessType: '', businessSpecialty: '',
    city: '', country: ''
  });
  const [locationDisplay, setLocationDisplay] = useState('');
  const [locationOptions, setLocationOptions] = useState([]);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2. Load and format city data when component mounts
  useEffect(() => {
    // Fetch all cities globally
    const allCities = City.getAllCities();

    // Create an array of country lookups to map countryCode -> countryName efficiently
    const countryMap = Country.getAllCountries().reduce((acc, country) => {
      acc[country.isoCode] = country.name;
      return  acc;
    }, {});

    // Format into unified strings: "New York, United States"
    const formattedLocations = allCities.map(city => {
      const countryName = countryMap[city.countryCode] || city.countryCode;
      return `${city.name}, ${countryName}`;
    });

    // Remove duplicates (some cities share names/countries)
    const uniqueLocations = Array.from(new Set(formattedLocations));
    setLocationOptions(uniqueLocations);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Sends clean form data directly with parsed city and country strings
      const { data } = await api.post('/auth/register-tenant', form);
      login(data.user, data.token);
      navigate('/tenant/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // 3. Parse "City, Country" selection back into individual values
  const handleLocationChange = (combinedValue) => {
    setLocationDisplay(combinedValue);

    if (combinedValue.includes(',')) {
      const parts = combinedValue.split(',');
      const city = parts[0].trim();
      // Combine remaining parts back just in case country name has commas
      const country = parts.slice(1).join(',').trim();

      setForm(prev => ({ ...prev, city, country }));
    } else {
      // Reset if user clears the input field
      setForm(prev => ({ ...prev, city: '', country: '' }));
    }
  };

  const f = (field) => ({
    value: form[field],
    onChange: e => setForm({ ...form, [field]: e.target.value }),
  });

  const selectedTypeObj = BUSINESS_TYPES.find(b => b.type === form.businessType);
  const specialtyOptions = selectedTypeObj ? selectedTypeObj.subtypes : [];

  return (
      <div className="page flex-center" style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, rgb(255,46,88) 0%, rgb(59,28,98) 100%)',
        padding: '5vh 0vw',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
      }}>
        <img src={'https://pub-267dd4744ace49cf8306de8e7c530c15.r2.dev/assets/Logo-White.png'}
             style={{width: '40vw', minWidth: 150, maxWidth: 180, marginBottom: 40}}/>
        <div className="card" style={{
          width: '100%',
          maxWidth: 440,
          background: 'rgba(255,255,255,0.12)',
          color: '#fff',
          borderColor: 'transparent'
        }}>
          <div className="text-center mb-3">
            <div style={{fontSize: 40}}>🚀</div>
            <h2 style={{marginTop: 8}}>Create Tenant Account</h2>
            <p style={{fontSize: 14}}>Start collecting feedback today</p>
          </div>
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="grid-2">
              <div className="form-group">
                <label style={{color: '#fff'}}>Your Name</label>
                <input className="form-control" required placeholder="John Doe" {...f('name')} />
              </div>
              <div className="form-group">
                <label style={{color: '#fff'}}>Business Name</label>
                <input className="form-control" required placeholder="Acme Corp" {...f('businessName')} />
              </div>
            </div>

            {/* REUSED YOUR DROPDOWN FOR LOCAL LOCATION DATA */}
            <SearchDropdown
                label="Location"
                placeholder="Search city..."
                options={locationOptions}
                value={locationDisplay}
                onChange={handleLocationChange}
            />

            <SearchDropdown
                label="Business Type"
                placeholder="Search or select your industry…"
                options={BUSINESS_TYPES.map(b => b.type)}
                value={form.businessType}
                onChange={(val) => setForm(prev => ({...prev, businessType: val, businessSpecialty: ''}))}
            />

            <SearchDropdown
                label="Specialty"
                placeholder={form.businessType ? `Search ${form.businessType} specialties…` : 'Select a business type first'}
                options={specialtyOptions}
                value={form.businessSpecialty}
                onChange={(val) => setForm(prev => ({...prev, businessSpecialty: val}))}
                disabled={!form.businessType}
            />

            <div className="form-group">
              <label style={{color: '#fff'}}>Email</label>
              <input className="form-control" type="email" required placeholder="you@business.com" {...f('email')} />
            </div>
            <div className="form-group">
              <label style={{color: '#fff'}}>Password</label>
              <input className="form-control" type="password" required
                     placeholder="Min 6 characters" {...f('password')} />
            </div>
            <button className="btn btn-primary" style={{width: '100%', justifyContent: 'center'}} type="submit"
                    disabled={loading}>
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>
          <p className="text-center mt-2" style={{fontSize: 14}}>
            Already registered? <Link to="/tenant/login" style={{color: '#fff', textDecoration: 'none'}}>Sign in</Link>
          </p>
          <p className="text-center mt-1" style={{fontSize: 14}}>
            <Link to="/" style={{color: '#fff', textDecoration: 'none'}}>← Back to home</Link>
          </p>
        </div>
      </div>
  );
}